// AdminApprovalPage.jsx - UPDATED WITH APPROVE/DISAPPROVE FEATURES
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './AdminApprovalPage.css';

const AdminApprovalPage = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState({});
  const [disapproving, setDisapproving] = useState({});
  const [suspending, setSuspending] = useState({});
  const [activating, setActivating] = useState({});
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'active', 'suspended'
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ type: '', userId: null, userName: '' });
  const [disapprovalReason, setDisapprovalReason] = useState('');
  const navigate = useNavigate();

  // Check if user is admin on component mount
  useEffect(() => {
    const checkAdminStatus = () => {
      try {
        const userStr = localStorage.getItem('nlngra_user');
        const token = localStorage.getItem('nlngra_token');
        
        if (!userStr || !token) {
          setError('Please login as admin first');
          setLoading(false);
          return false;
        }

        const userData = JSON.parse(userStr);
        setUser(userData);

        // Check if user is admin
        if (userData.account_type === 'admin') {
          setIsAdmin(true);
          return true;
        } else {
          setError(`Access denied. Your account type is: ${userData.account_type}. Admin access required.`);
          setLoading(false);
          return false;
        }
      } catch (error) {
        console.error('Admin check error:', error);
        setError('Error checking admin status');
        setLoading(false);
        return false;
      }
    };

    if (!checkAdminStatus()) {
      return;
    }

    // Fetch users if admin
    fetchUsers();
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get auth token
      const token = localStorage.getItem('nlngra_token');
      
      if (!token) {
        setError('Please login as admin first');
        setLoading(false);
        return;
      }
      
      let endpoint = '';
      switch(activeTab) {
        case 'pending':
          endpoint = 'pending-users';
          break;
        case 'active':
          endpoint = 'active-users';
          break;
        case 'suspended':
          endpoint = 'suspended-users';
          break;
        default:
          endpoint = 'all-users';
      }
      
      const response = await fetch(`/api/index.php?url=${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      // Clean PHP warnings
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let data;
      
      if (jsonMatch) {
        data = JSON.parse(jsonMatch[0]);
      } else {
        data = JSON.parse(cleanResponse);
      }
      
      console.log('Parsed data:', data);
      
      if (data.success) {
        if (activeTab === 'pending') {
          setPendingUsers(data.users || []);
        } else {
          setAllUsers(data.users || []);
        }
      } else {
        setError(data.error || data.message || 'Failed to fetch users');
      }
      
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Network error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      setApproving(prev => ({ ...prev, [userId]: true }));
      
      const token = localStorage.getItem('nlngra_token');
      
      const response = await fetch('/api/index.php?url=approve-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId })
      });
      
      const responseText = await response.text();
      
      // Clean PHP warnings
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let data;
      
      if (jsonMatch) {
        data = JSON.parse(jsonMatch[0]);
      } else {
        data = JSON.parse(cleanResponse);
      }
      
      if (data.success) {
        alert(`✅ User approved successfully!`);
        
        // Remove approved user from list
        setPendingUsers(prev => prev.filter(user => user.id !== userId));
        
        // Refresh list after 1 second
        setTimeout(() => {
          fetchUsers();
        }, 1000);
      } else {
        alert('❌ Failed to approve user: ' + (data.error || data.message));
      }
      
    } catch (error) {
      console.error('Error approving user:', error);
      alert('❌ Error: ' + error.message);
    } finally {
      setApproving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const disapproveUser = async (userId, reason = '') => {
    try {
      setDisapproving(prev => ({ ...prev, [userId]: true }));
      
      const token = localStorage.getItem('nlngra_token');
      
      const response = await fetch('/api/index.php?url=disapprove-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          user_id: userId,
          reason: reason || 'No reason provided'
        })
      });
      
      const responseText = await response.text();
      
      // Clean PHP warnings
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let data;
      
      if (jsonMatch) {
        data = JSON.parse(jsonMatch[0]);
      } else {
        data = JSON.parse(cleanResponse);
      }
      
      if (data.success) {
        alert(`❌ User disapproved successfully!`);
        
        // Remove disapproved user from list
        setPendingUsers(prev => prev.filter(user => user.id !== userId));
        
        // Refresh list after 1 second
        setTimeout(() => {
          fetchUsers();
        }, 1000);
      } else {
        alert('❌ Failed to disapprove user: ' + (data.error || data.message));
      }
      
    } catch (error) {
      console.error('Error disapproving user:', error);
      alert('❌ Error: ' + error.message);
    } finally {
      setDisapproving(prev => ({ ...prev, [userId]: false }));
      setShowModal(false);
      setDisapprovalReason('');
    }
  };

  const suspendUser = async (userId) => {
    try {
      setSuspending(prev => ({ ...prev, [userId]: true }));
      
      const token = localStorage.getItem('nlngra_token');
      
      const response = await fetch('/api/index.php?url=suspend-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId })
      });
      
      const responseText = await response.text();
      
      // Clean PHP warnings
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let data;
      
      if (jsonMatch) {
        data = JSON.parse(jsonMatch[0]);
      } else {
        data = JSON.parse(cleanResponse);
      }
      
      if (data.success) {
        alert(`⏸️ User suspended successfully!`);
        
        // Refresh list
        setTimeout(() => {
          fetchUsers();
        }, 1000);
      } else {
        alert('❌ Failed to suspend user: ' + (data.error || data.message));
      }
      
    } catch (error) {
      console.error('Error suspending user:', error);
      alert('❌ Error: ' + error.message);
    } finally {
      setSuspending(prev => ({ ...prev, [userId]: false }));
    }
  };

  const activateUser = async (userId) => {
    try {
      setActivating(prev => ({ ...prev, [userId]: true }));
      
      const token = localStorage.getItem('nlngra_token');
      
      const response = await fetch('/api/index.php?url=activate-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId })
      });
      
      const responseText = await response.text();
      
      // Clean PHP warnings
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let data;
      
      if (jsonMatch) {
        data = JSON.parse(jsonMatch[0]);
      } else {
        data = JSON.parse(cleanResponse);
      }
      
      if (data.success) {
        alert(`✅ User activated successfully!`);
        
        // Refresh list
        setTimeout(() => {
          fetchUsers();
        }, 1000);
      } else {
        alert('❌ Failed to activate user: ' + (data.error || data.message));
      }
      
    } catch (error) {
      console.error('Error activating user:', error);
      alert('❌ Error: ' + error.message);
    } finally {
      setActivating(prev => ({ ...prev, [userId]: false }));
    }
  };

  const showDisapprovalModal = (userId, userName) => {
    setModalData({
      type: 'disapprove',
      userId,
      userName
    });
    setShowModal(true);
  };

  const handleDisapproveConfirm = () => {
    if (modalData.userId) {
      disapproveUser(modalData.userId, disapprovalReason);
    }
  };

  const showDeleteModal = (userId, userName) => {
    setModalData({
      type: 'delete',
      userId,
      userName
    });
    setShowModal(true);
  };

  const deleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('nlngra_token');
      
      const response = await fetch('/api/index.php?url=delete-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId })
      });
      
      const responseText = await response.text();
      
      // Clean PHP warnings
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let data;
      
      if (jsonMatch) {
        data = JSON.parse(jsonMatch[0]);
      } else {
        data = JSON.parse(cleanResponse);
      }
      
      if (data.success) {
        alert(`🗑️ User deleted successfully!`);
        
        // Refresh list
        setTimeout(() => {
          fetchUsers();
        }, 1000);
      } else {
        alert('❌ Failed to delete user: ' + (data.error || data.message));
      }
      
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('❌ Error: ' + error.message);
    } finally {
      setShowModal(false);
    }
  };

  const handleDeleteConfirm = () => {
    if (modalData.userId) {
      deleteUser(modalData.userId);
    }
  };

  const logout = () => {
    localStorage.removeItem('nlngra_token');
    localStorage.removeItem('nlngra_user');
    localStorage.removeItem('nlngra_jwt');
    localStorage.removeItem('nlngra_session');
    navigate('/login');
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  // If not admin, show access denied
  if (!isAdmin && !loading) {
    return (
      <div className="admin-access-denied">
        <motion.div
          className="denied-card"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="denied-icon">🚫</div>
          <h2>Admin Access Required</h2>
          <p>You need administrator privileges to access this page.</p>
          
          {user ? (
            <>
              <div className="user-info">
                <p><strong>Logged in as:</strong> {user.email}</p>
                <p><strong>Account Type:</strong> {user.account_type}</p>
                <p><strong>Status:</strong> {user.status}</p>
              </div>
              
              <div className="denied-actions">
                <button onClick={goToDashboard} className="btn btn-primary">
                  Go to Dashboard
                </button>
                <button onClick={logout} className="btn btn-outline">
                  Logout
                </button>
              </div>
            </>
          ) : (
            <div className="denied-actions">
              <button onClick={() => navigate('/login')} className="btn btn-primary">
                Login as Admin
              </button>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  const renderUsers = () => {
    const users = activeTab === 'pending' ? pendingUsers : allUsers;
    
    if (users.length === 0) {
      return (
        <div className="no-users">
          <div className="success-icon">✅</div>
          <h3>No {activeTab} Users</h3>
          <p>There are no {activeTab} users at the moment.</p>
        </div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div className="stats-card">
          <div className="stat">
            <span className="stat-number">{users.length}</span>
            <span className="stat-label">
              {activeTab === 'pending' ? 'Pending' : activeTab === 'active' ? 'Active' : 'Suspended'} Users
            </span>
          </div>
          <div className="stat">
            <span className="stat-number">
              {new Date().toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
            <span className="stat-label">Today</span>
          </div>
        </div>
        
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Membership No.</th>
                <th>Type</th>
                <th>Status</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <motion.tr 
                  key={user.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  whileHover={{ backgroundColor: 'rgba(10, 61, 43, 0.02)' }}
                >
                  <td className="user-id">#{user.id}</td>
                  <td className="user-name">
                    <strong>{user.first_name} {user.last_name}</strong>
                  </td>
                  <td className="user-email">{user.email}</td>
                  <td className="user-phone">{user.phone || 'N/A'}</td>
                  <td className="user-membership">{user.membership_number || 'N/A'}</td>
                  <td className="user-type">
                    <span className={`type-badge ${user.account_type || user.membership_type}`}>
                      {user.account_type || user.membership_type || 'N/A'}
                    </span>
                  </td>
                  <td className="user-status">
                    <span className={`status-badge ${user.status}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="user-date">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="user-actions">
                    <div className="action-buttons">
                      {activeTab === 'pending' ? (
                        <>
                          <motion.button 
                            onClick={() => approveUser(user.id)} 
                            className="btn-action btn-approve"
                            disabled={approving[user.id]}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {approving[user.id] ? (
                              <>
                                <span className="spinner-small"></span>
                                Approving...
                              </>
                            ) : (
                              '✅ Approve'
                            )}
                          </motion.button>
                          
                          <motion.button 
                            onClick={() => showDisapprovalModal(user.id, `${user.first_name} ${user.last_name}`)} 
                            className="btn-action btn-disapprove"
                            disabled={disapproving[user.id]}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {disapproving[user.id] ? (
                              <>
                                <span className="spinner-small"></span>
                                Disapproving...
                              </>
                            ) : (
                              '❌ Disapprove'
                            )}
                          </motion.button>
                        </>
                      ) : activeTab === 'active' ? (
                        <>
                          <motion.button 
                            onClick={() => suspendUser(user.id)} 
                            className="btn-action btn-suspend"
                            disabled={suspending[user.id]}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {suspending[user.id] ? (
                              <>
                                <span className="spinner-small"></span>
                                Suspending...
                              </>
                            ) : (
                              '⏸️ Suspend'
                            )}
                          </motion.button>
                          
                          <motion.button 
                            onClick={() => showDeleteModal(user.id, `${user.first_name} ${user.last_name}`)} 
                            className="btn-action btn-delete"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            🗑️ Delete
                          </motion.button>
                        </>
                      ) : (
                        <>
                          <motion.button 
                            onClick={() => activateUser(user.id)} 
                            className="btn-action btn-activate"
                            disabled={activating[user.id]}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {activating[user.id] ? (
                              <>
                                <span className="spinner-small"></span>
                                Activating...
                              </>
                            ) : (
                              '✅ Activate'
                            )}
                          </motion.button>
                          
                          <motion.button 
                            onClick={() => showDeleteModal(user.id, `${user.first_name} ${user.last_name}`)} 
                            className="btn-action btn-delete"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            🗑️ Delete
                          </motion.button>
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="table-footer">
          <p>Total {activeTab} users: <strong>{users.length}</strong></p>
          <p className="note">
            {activeTab === 'pending' 
              ? 'Note: Approved users will be automatically removed from this list.'
              : 'Use actions to manage user accounts.'}
          </p>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div 
      className="admin-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="admin-header">
        <h2>👑 Admin Management Panel</h2>
        <div className="admin-user-info">
          {user && (
            <div className="user-badge">
              <span className="user-email">{user.email}</span>
              <span className="user-role">({user.account_type})</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="admin-actions">
        <motion.button 
          onClick={fetchUsers} 
          className="btn-refresh"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          🔄 Refresh List
        </motion.button>
        
        <div className="admin-controls">
          <button onClick={goToDashboard} className="btn-control">
            📊 Dashboard
          </button>
          <button onClick={logout} className="btn-control logout">
            🚪 Logout
          </button>
        </div>
      </div>
      
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          ⏳ Pending ({pendingUsers.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          ✅ Active
        </button>
        <button 
          className={`tab-btn ${activeTab === 'suspended' ? 'active' : ''}`}
          onClick={() => setActiveTab('suspended')}
        >
          ⏸️ Suspended
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
          {error.includes('login') && (
            <p>
              <button 
                onClick={() => navigate('/login')} 
                className="btn-link"
              >
                Click here to login
              </button>
            </p>
          )}
        </div>
      )}
      
      {loading ? (
        <div className="loading-state">
          <motion.div
            className="spinner"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          >
            ⏳
          </motion.div>
          <p>Loading users...</p>
        </div>
      ) : (
        renderUsers()
      )}

      {/* Disapproval/Delete Modal */}
      {showModal && (
        <div className="modal-overlay">
          <motion.div 
            className="modal-content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <h3>
              {modalData.type === 'disapprove' ? 'Disapprove User' : 'Delete User'}
            </h3>
            
            <p>
              Are you sure you want to {modalData.type === 'disapprove' ? 'disapprove' : 'delete'}{' '}
              <strong>{modalData.userName}</strong>?
            </p>
            
            {modalData.type === 'disapprove' && (
              <div className="modal-input">
                <label htmlFor="disapprovalReason">Reason for disapproval:</label>
                <textarea
                  id="disapprovalReason"
                  value={disapprovalReason}
                  onChange={(e) => setDisapprovalReason(e.target.value)}
                  placeholder="Optional: Enter reason for disapproval..."
                  rows={3}
                />
              </div>
            )}
            
            {modalData.type === 'delete' && (
              <div className="warning-message">
                ⚠️ <strong>Warning:</strong> This action cannot be undone. All user data will be permanently deleted.
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                onClick={() => {
                  setShowModal(false);
                  setDisapprovalReason('');
                }} 
                className="btn-cancel"
              >
                Cancel
              </button>
              <button 
                onClick={modalData.type === 'disapprove' ? handleDisapproveConfirm : handleDeleteConfirm}
                className={modalData.type === 'disapprove' ? 'btn-disapprove' : 'btn-delete'}
              >
                {modalData.type === 'disapprove' ? 'Disapprove' : 'Delete Permanently'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminApprovalPage;